import { useCallback, useEffect, useRef } from "react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

// ─── Types ─────────────────────────────────────────────────────────────────

interface MotionScore {
  second: number
  score: number
}

interface MatchEvent {
  id: number
  minute: number
  event_type: string
  team: string
  confidence: string
  shirt_number: number | null
  detail: string | null
  player_name: string | null
}

interface MotionData {
  scores: MotionScore[]
  play_segments: number[][]
  whistle_timestamps: { time: number; duration: number; ratio: number }[]
  total_play_seconds: number
  total_duration_seconds: number
}

interface TimelineProps {
  motionData: MotionData
  events: MatchEvent[]
  onSeek: (seconds: number) => void
  currentTime: number
  homeTeamName: string
  awayTeamName: string
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}

// Simple 2-zone: dead = dark, play = bright. Sharp boundary, minimal colors.
function thermalColor(t: number): [number, number, number] {
  if (t < 0.3) return lerp3([30, 30, 40], [50, 55, 70], t / 0.3)            // dead: dark grey
  if (t < 0.45) return lerp3([50, 55, 70], [100, 70, 20], (t - 0.3) / 0.15) // transition zone
  return lerp3([180, 120, 20], [250, 200, 50], Math.min((t - 0.45) / 0.4, 1)) // play: orange → amber
}

function lerp3(
  a: [number, number, number],
  b: [number, number, number],
  t: number
): [number, number, number] {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ]
}

const CONFIDENCE_COLORS: Record<string, string> = {
  high: "bg-emerald-500 hover:bg-emerald-400",
  medium: "bg-amber-500 hover:bg-amber-400",
  low: "bg-red-400 hover:bg-red-300",
}

const CONFIDENCE_ICONS: Record<string, string> = {
  high: "✅",
  medium: "⚠️",
  low: "❓",
}

const EVENT_LABELS: Record<string, string> = {
  try: "Try",
  conversion: "Conv",
  conversion_miss: "Conv ✗",
  penalty_goal: "Pen",
  penalty_kick_miss: "Pen ✗",
  drop_goal: "DG",
  yellow_card: "🟨",
  red_card: "🟥",
  scrum: "Scrum",
  lineout: "LO",
  knock_on: "KO",
  tackle: "Tckl",
  break: "Brk",
  turnover: "TO",
  kickoff: "K/O",
  restart: "Rst",
  halftime: "HT",
  fulltime: "FT",
}

// ─── Gradient Legend ───────────────────────────────────────────────────────

function GradientLegend() {
  const stops = [
    { pos: 0, label: "No play" },
    { pos: 30, label: "Low" },
    { pos: 50, label: "" },
    { pos: 70, label: "Active" },
    { pos: 100, label: "High" },
  ]

  return (
    <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
      <div className="relative h-2 w-32 rounded-sm overflow-hidden border border-neutral-300">
        {Array.from({ length: 32 }, (_, i) => {
          const t = i / 31
          const [r, g, b] = thermalColor(t)
          return (
            <div
              key={i}
              className="absolute top-0 h-full"
              style={{
                left: `${t * 100}%`,
                width: "4%",
                backgroundColor: `rgb(${r}, ${g}, ${b})`,
              }}
            />
          )
        })}
      </div>
      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm" style={{backgroundColor:'rgb(40,40,55)'}}/> Dead time</span>
      <span className="text-neutral-300">→</span>
      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm" style={{backgroundColor:'rgb(220,160,30)'}}/> Play</span>
    </div>
  )
}

// ─── Motion Heatmap Strip ──────────────────────────────────────────────────

function MotionStrip({
  motionData,
  onSeek,
  currentTime,
}: {
  motionData: MotionData
  onSeek: (s: number) => void
  currentTime: number
}) {
  const { scores, play_segments, total_duration_seconds } = motionData
  const ref = useRef<HTMLDivElement>(null)

  if (!scores.length || total_duration_seconds === 0) return null

  // Normalize relative to a meaningful reference, not the absolute max.
  // Use 90th percentile as the "hot" anchor so the gradient uses its full range.
  const sorted = [...scores.map((s) => s.score)].sort((a, b) => a - b)
  const p90 = sorted[Math.floor(sorted.length * 0.9)] || 1
  const refScore = Math.max(p90, 1)

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    const pct = (e.clientX - rect.left) / rect.width
    onSeek(Math.round(pct * total_duration_seconds))
  }

  const playheadPct = (currentTime / total_duration_seconds) * 100

  // Smooth scores with a 5-second rolling average to reduce noise
  const smoothed = scores.map((s, i) => {
    const window = 5
    const start = Math.max(0, i - window)
    const end = Math.min(scores.length - 1, i + window)
    const slice = scores.slice(start, end + 1)
    const avg = slice.reduce((sum, x) => sum + x.score, 0) / slice.length
    return { second: s.second, score: avg }
  })

  // Draw heatmap once into canvas, only redraw when data changes
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    canvas.width = canvas.offsetWidth * dpr
    canvas.height = 8 * dpr
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const barW = canvas.width / total_duration_seconds
    for (let i = 0; i < smoothed.length; i++) {
      const t = Math.min(smoothed[i].score / refScore, 1)
      const [r, g, b] = thermalColor(t)
      ctx.fillStyle = `rgb(${r},${g},${b})`
      ctx.fillRect(Math.round(i * barW), 0, Math.ceil(barW) + 1, canvas.height)
    }
  }, [total_duration_seconds, refScore, JSON.stringify(smoothed)])

  return (
    <div
      ref={ref}
      onClick={handleClick}
      className="relative cursor-crosshair"
    >
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: "8px" }}
        className="rounded-sm border border-neutral-600"
      />

      {/* Playhead line */}
      <div
        className="absolute top-0 h-8 w-0.5 bg-white shadow-[0_0_3px_rgba(0,0,0,0.9)] pointer-events-none z-10"
        style={{ left: `${playheadPct}%` }}
      />

      {/* Playhead top triangle marker */}
      <div
        className="absolute -top-0.5 h-0 w-0 pointer-events-none z-10"
        style={{
          left: `${playheadPct}%`,
          borderTop: "5px solid white",
          borderLeft: "3px solid transparent",
          borderRight: "3px solid transparent",
          filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.5))",
        }}
      />

      {/* Play segment overlay */}
      <div className="absolute inset-0 flex h-8">
        {play_segments.map((seg, i) => {
          const left = (seg[0] / total_duration_seconds) * 100
          const width = ((seg[1] - seg[0]) / total_duration_seconds) * 100
          return (
            <TooltipProvider key={i}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="absolute top-0 h-full border-t-2 border-t-sky-400"
                    style={{ left: `${left}%`, width: `${width}%` }}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  ▶ {formatTime(seg[0])} – {formatTime(seg[1])} ({seg[1] - seg[0]}s)
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        })}
      </div>

      {/* Whistle markers — dedicated row below heatmap */}
      <div className="relative h-3 w-full">
        {motionData.whistle_timestamps.map((w, i) => {
          const left = (w.time / total_duration_seconds) * 100
          return (
            <TooltipProvider key={`w-${i}`}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="absolute top-0 h-full w-2 -translate-x-1/2 cursor-pointer rounded-sm bg-cyan-400 hover:bg-cyan-300 shadow-sm"
                    style={{ left: `${left}%` }}
                    onClick={() => onSeek(Math.round(w.time))}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  📣 Whistle at {formatTime(w.time)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        })}
      </div>

      {/* Time labels */}
      <div className="text-muted-foreground mt-0.5 flex justify-between font-mono text-[10px] leading-none">
        <span>0:00</span>
        <span>{formatTime(total_duration_seconds)}</span>
      </div>
    </div>
  )
}

// ─── Event Markers Strip ───────────────────────────────────────────────────

function EventStrip({
  events,
  totalDurationSeconds,
  onSeek,
  homeTeamName,
  awayTeamName,
}: {
  events: MatchEvent[]
  totalDurationSeconds: number
  onSeek: (seconds: number) => void
  homeTeamName: string
  awayTeamName: string
}) {
  if (!events.length || totalDurationSeconds === 0) return null

  const minuteToSecond = (minute: number) => minute * 63

  return (
    <div className="relative h-5 w-full">
      {events.map((event) => {
        const videoSecond = minuteToSecond(event.minute)
        const left = (videoSecond / totalDurationSeconds) * 100
        const colorClass =
          CONFIDENCE_COLORS[event.confidence] || CONFIDENCE_COLORS.low

        return (
          <TooltipProvider key={event.id}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className={`absolute top-0 h-full w-2 cursor-pointer rounded-sm shadow-sm transition-transform hover:scale-y-150 ${colorClass}`}
                  style={{ left: `${left}%` }}
                  onClick={() => onSeek(videoSecond)}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">
                <div>
                  <span className="font-semibold">
                    {CONFIDENCE_ICONS[event.confidence]} {event.minute}'
                  </span>{" "}
                  {EVENT_LABELS[event.event_type] || event.event_type} —{" "}
                  {event.team === "home" ? homeTeamName : awayTeamName}
                  {event.shirt_number ? ` #${event.shirt_number}` : ""}
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )
      })}
    </div>
  )
}

// ─── Event List (compact, scrollable) ──────────────────────────────────────

function EventList({
  events,
  onSeek,
  homeTeamName,
  awayTeamName,
}: {
  events: MatchEvent[]
  onSeek: (seconds: number) => void
  homeTeamName: string
  awayTeamName: string
}) {
  if (!events.length) {
    return (
      <div className="text-muted-foreground flex h-full items-center justify-center py-8 text-sm">
        No events recorded yet.
      </div>
    )
  }

  return (
    <div
      className="flex flex-col divide-y overflow-y-auto"
      style={{ maxHeight: "calc(100vh - 420px)" }}
    >
      {events.map((event) => {
        const colorMap: Record<string, string> = {
          high: "border-l-emerald-500 bg-emerald-50/50 hover:bg-emerald-100/50",
          medium: "border-l-amber-500 bg-amber-50/50 hover:bg-amber-100/50",
          low: "border-l-red-400 bg-red-50/50 hover:bg-red-100/50",
        }
        const color = colorMap[event.confidence] || colorMap.low

        return (
          <button
            key={event.id}
            onClick={() => onSeek(event.minute * 63)}
            className={`flex cursor-pointer items-start gap-2 border-l-2 px-3 py-1.5 text-left transition-colors ${color}`}
          >
            <span className="font-mono text-xs font-semibold tabular-nums text-muted-foreground w-6 shrink-0">
              {event.minute}'
            </span>
            <div className="min-w-0 text-xs">
              <span className="font-medium">
                {event.team === "home" ? homeTeamName : awayTeamName}
              </span>{" "}
              <span className="text-muted-foreground">
                {EVENT_LABELS[event.event_type] || event.event_type}
              </span>
              {event.shirt_number && (
                <span className="text-muted-foreground">
                  {" "}
                  #{event.shirt_number}
                </span>
              )}
              {event.detail && (
                <span className="text-muted-foreground block truncate text-[11px]">
                  {event.detail}
                </span>
              )}
            </div>
            <span className="ml-auto shrink-0 text-[10px]">
              {CONFIDENCE_ICONS[event.confidence]}
            </span>
          </button>
        )
      })}
    </div>
  )
}

// ─── Main Timeline ─────────────────────────────────────────────────────────

export default function Timeline({
  motionData,
  events,
  onSeek,
  currentTime,
  homeTeamName,
  awayTeamName,
}: TimelineProps) {
  const hasMotionData =
    motionData.scores.length > 0 && motionData.total_duration_seconds > 0

  const totalPlayMin = Math.round(motionData.total_play_seconds / 60)
  const totalMin = Math.round(motionData.total_duration_seconds / 60)

  return (
    <div className="space-y-2">
      {/* Stats bar + legend */}
      <div className="flex items-center justify-between">
        <div className="flex gap-3 text-xs text-muted-foreground">
          {hasMotionData ? (
            <>
              <span>
                ▶ {totalPlayMin}m play / {totalMin}m total
              </span>
              <span>•</span>
              <span>{motionData.whistle_timestamps.length} whistles</span>
              <span>{motionData.play_segments.length} segments</span>
            </>
          ) : (
            <span className="italic">No motion data</span>
          )}
        </div>
        <GradientLegend />
      {motionData.whistle_timestamps.length > 0 && (
        <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-sm bg-cyan-400" />
          Whistle
        </span>
      )}
      </div>

      {/* Motion strip — clickable to seek, with playhead */}
      {hasMotionData && (
        <MotionStrip
          motionData={motionData}
          onSeek={onSeek}
          currentTime={currentTime}
        />
      )}

      {/* Event markers strip — aligned with motion strip */}
      {hasMotionData && events.length > 0 && (
        <EventStrip
          events={events}
          totalDurationSeconds={motionData.total_duration_seconds}
          onSeek={onSeek}
          homeTeamName={homeTeamName}
          awayTeamName={awayTeamName}
        />
      )}

      {/* Event count + confidence summary */}
      {events.length > 0 && (
        <div className="flex gap-2 text-xs text-muted-foreground">
          {(["high", "medium", "low"] as const).map((c) => {
            const count = events.filter((e) => e.confidence === c).length
            if (!count) return null
            const dotColor =
              c === "high"
                ? "bg-emerald-500"
                : c === "medium"
                  ? "bg-amber-500"
                  : "bg-red-400"
            return (
              <span key={c} className="flex items-center gap-1">
                <span
                  className={`inline-block h-1.5 w-1.5 rounded-full ${dotColor}`}
                />
                {count} {c}
              </span>
            )
          })}
        </div>
      )}

      {/* Event list */}
      <EventList
        events={events}
        onSeek={onSeek}
        homeTeamName={homeTeamName}
        awayTeamName={awayTeamName}
      />
    </div>
  )
}
